import { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Search, Printer, Calendar, Package, Phone, MapPin, CheckCircle2, Clock, MessageSquare, RefreshCw, XCircle, Truck, ArrowUpDown, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatPrice } from '../lib/utils';

// Status options for customers
const STATUS_OPTIONS = [
    { id: 'mai_sta', label: 'Mai stă', color: 'bg-amber-100 text-amber-700 border-amber-300' },
    { id: 'asteapta_raspuns', label: 'Așteaptă răspuns', color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { id: 'de_pregatit', label: 'De pregătit', color: 'bg-green-100 text-green-700 border-green-300' },
    { id: 'de_anulat', label: 'De anulat', color: 'bg-red-100 text-red-700 border-red-300' },
    { id: 'de_intrebat', label: 'De întrebat', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
    { id: 'de_livrat', label: 'De livrat', color: 'bg-purple-100 text-purple-700 border-purple-300', hasDate: true },
    { id: 'comanda_mica', label: 'Comandă mică', color: 'bg-orange-100 text-orange-700 border-orange-300' }
];

const ControlClienti = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('box'); // 'box', 'customer', 'value', 'status'
    const [activeFilter, setActiveFilter] = useState(null); // null = all, or status id like 'de_pregatit'
    const [customerStatuses, setCustomerStatuses] = useState({});
    const [customerNotes, setCustomerNotes] = useState({});
    const [editingNote, setEditingNote] = useState(null);
    const [tempNote, setTempNote] = useState('');
    const [deliveryDatePicker, setDeliveryDatePicker] = useState(null); // customer_id for date picker
    const printRef = useRef(null);

    // Load customers with reservations/orders
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            // Get reservations
            const [reservationsRes, ordersRes, statusesRes] = await Promise.all([
                api.get('/reservations/v2'),
                api.get('/orders/v2/customers'),
                api.get('/customer-control/statuses')
            ]);

            // Combine and deduplicate customers
            const reservations = reservationsRes.data || [];
            const orders = ordersRes.data || [];
            
            const customerMap = new Map();
            
            // Add from reservations - EXCLUDE virtual/unconfirmed orders (customer_id with more than 1 letter)
            reservations.forEach(r => {
                const customerId = r.customer_id || '';
                // Check if virtual (more than 1 letter in customer_id)
                const letterCount = (customerId.match(/[a-zA-Z]/g) || []).length;
                const isVirtual = letterCount > 1;
                
                // Skip virtual/unconfirmed orders
                if (isVirtual) return;
                
                const existing = customerMap.get(r.customer_id) || {
                    customer_id: r.customer_id,
                    name: r.name || '',
                    phone: r.phone || '',
                    address: r.address || '',
                    box_number: null,
                    box_assigned_at: null,
                    total_qty: 0,
                    total_value: 0,
                    source: 'rezervare',
                    created_at: null
                };
                
                existing.name = existing.name || r.name || '';
                existing.phone = existing.phone || r.phone || '';
                existing.address = existing.address || r.address || '';
                existing.total_qty += r.total_qty || 0;
                existing.total_value += r.total_value || 0;
                
                // Get box info if available - check multiple possible structures
                if (r.boxes && r.boxes.length > 0) {
                    const box = r.boxes[0];
                    existing.box_number = box.box_number || box.number || box.name || null;
                    existing.box_assigned_at = box.assigned_at;
                } else if (r.box_number) {
                    // Direct box_number on reservation
                    existing.box_number = r.box_number;
                }
                
                // Track earliest date
                if (r.items && r.items.length > 0) {
                    const itemDates = r.items.map(i => i.created_at).filter(Boolean);
                    if (itemDates.length > 0) {
                        const earliest = itemDates.sort()[0];
                        if (!existing.created_at || earliest < existing.created_at) {
                            existing.created_at = earliest;
                        }
                    }
                }
                
                customerMap.set(r.customer_id, existing);
            });
            
            // Add from orders (if not already in reservations)
            orders.forEach(o => {
                if (!customerMap.has(o.customer_id) && o.item_count > 0) {
                    customerMap.set(o.customer_id, {
                        customer_id: o.customer_id,
                        name: o.name || '',
                        phone: o.phone || '',
                        address: o.address || '',
                        box_number: 'C', // "C" for orders (Comandă)
                        box_assigned_at: null,
                        total_qty: o.item_count || 0,
                        total_value: o.total_value || 0,
                        source: 'comandă',
                        created_at: null
                    });
                }
            });
            
            // Convert to array - filter out customers with 0 value
            const customerList = Array.from(customerMap.values())
                .filter(c => c.total_qty > 0 && c.total_value > 0);
            
            setCustomers(customerList);
            
            // Load saved statuses
            const statuses = statusesRes.data || {};
            setCustomerStatuses(statuses.statuses || {});
            setCustomerNotes(statuses.notes || {});
            
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Eroare la încărcare date');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Toggle status for a customer
    const toggleStatus = async (customerId, statusId, deliveryDate = null) => {
        try {
            const currentStatuses = customerStatuses[customerId] || {};
            const isCurrentlyChecked = currentStatuses[statusId];
            
            // For de_livrat, we need a date
            let timestamp = null;
            if (!isCurrentlyChecked) {
                if (statusId === 'de_livrat' && deliveryDate) {
                    // Store both the check timestamp and the delivery date
                    timestamp = JSON.stringify({
                        checked_at: new Date().toISOString(),
                        delivery_date: deliveryDate.toISOString()
                    });
                } else {
                    timestamp = new Date().toISOString();
                }
            }
            
            const newStatuses = {
                ...customerStatuses,
                [customerId]: {
                    ...currentStatuses,
                    [statusId]: timestamp
                }
            };
            
            setCustomerStatuses(newStatuses);
            setDeliveryDatePicker(null);
            
            // Save to backend
            await api.post('/customer-control/statuses', {
                customer_id: customerId,
                status_id: statusId,
                checked: !isCurrentlyChecked,
                timestamp: timestamp
            });
            
            if (!isCurrentlyChecked) {
                const statusLabel = STATUS_OPTIONS.find(s => s.id === statusId)?.label;
                if (statusId === 'de_livrat' && deliveryDate) {
                    toast.success(`${statusLabel} - ${deliveryDate.toLocaleDateString('ro-RO')}`);
                } else {
                    toast.success(`${statusLabel} marcat`);
                }
            }
        } catch (err) {
            console.error('Error saving status:', err);
            toast.error('Eroare la salvare status');
        }
    };

    // Save note for a customer
    const saveNote = async (customerId) => {
        try {
            const newNotes = {
                ...customerNotes,
                [customerId]: tempNote
            };
            setCustomerNotes(newNotes);
            setEditingNote(null);
            
            await api.post('/customer-control/notes', {
                customer_id: customerId,
                note: tempNote
            });
            
            toast.success('Observație salvată');
        } catch (err) {
            console.error('Error saving note:', err);
            toast.error('Eroare la salvare observație');
        }
    };

    // Reset all statuses (clear all checkboxes)
    const resetAllStatuses = async () => {
        if (!window.confirm('Sigur vrei să resetezi toate bifele? Această acțiune nu poate fi anulată.')) {
            return;
        }
        
        try {
            await api.post('/customer-control/reset-all');
            setCustomerStatuses({});
            toast.success('Toate bifele au fost resetate');
        } catch (err) {
            console.error('Error resetting statuses:', err);
            toast.error('Eroare la resetare');
        }
    };

    // Format date nicely
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('ro-RO', { 
            day: '2-digit', 
            month: '2-digit',
            year: '2-digit'
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('ro-RO', { 
            day: '2-digit', 
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Parse delivery date from status (handles both old format and new JSON format)
    const parseDeliveryDate = (statusValue) => {
        if (!statusValue) return null;
        try {
            const parsed = JSON.parse(statusValue);
            return parsed.delivery_date ? new Date(parsed.delivery_date) : null;
        } catch {
            return null; // Old format, no delivery date
        }
    };

    const formatStatusTime = (statusValue) => {
        if (!statusValue) return '-';
        try {
            const parsed = JSON.parse(statusValue);
            if (parsed.delivery_date) {
                return new Date(parsed.delivery_date).toLocaleDateString('ro-RO');
            }
            return formatDateTime(parsed.checked_at);
        } catch {
            return formatDateTime(statusValue);
        }
    };

    // Sort helper functions
    const sortCustomers = (list) => {
        return [...list].sort((a, b) => {
            if (sortBy === 'box') {
                const getBoxNum = (box) => {
                    if (!box) return Infinity;
                    const num = parseInt(String(box).replace(/\D/g, ''));
                    return isNaN(num) ? Infinity : num;
                };
                const aBox = getBoxNum(a.box_number);
                const bBox = getBoxNum(b.box_number);
                if (aBox !== bBox) return aBox - bBox;
            } else if (sortBy === 'customer') {
                const aNum = parseInt(a.customer_id);
                const bNum = parseInt(b.customer_id);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return aNum - bNum;
                }
                return a.customer_id.localeCompare(b.customer_id);
            } else if (sortBy === 'value') {
                return b.total_value - a.total_value; // Descending
            } else if (sortBy === 'status') {
                // Sort by status count (most statuses first), then by specific priorities
                const aStatuses = customerStatuses[a.customer_id] || {};
                const bStatuses = customerStatuses[b.customer_id] || {};
                
                // Priority order: de_pregatit > de_livrat > asteapta_raspuns > de_intrebat > de_anulat > mai_sta > comanda_mica > no status
                const getPriority = (statuses) => {
                    if (statuses.de_pregatit) return 1;
                    if (statuses.de_livrat) return 2;
                    if (statuses.asteapta_raspuns) return 3;
                    if (statuses.de_intrebat) return 4;
                    if (statuses.de_anulat) return 5;
                    if (statuses.mai_sta) return 6;
                    if (statuses.comanda_mica) return 7;
                    return 99; // No status
                };
                
                const aPriority = getPriority(aStatuses);
                const bPriority = getPriority(bStatuses);
                if (aPriority !== bPriority) return aPriority - bPriority;
            }
            // Fallback
            const aNum = parseInt(a.customer_id);
            const bNum = parseInt(b.customer_id);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
            }
            return a.customer_id.localeCompare(b.customer_id);
        });
    };

    // Filter and sort customers
    const filteredCustomers = sortCustomers(customers.filter(c => {
        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            const matchesSearch = (
                c.customer_id.toLowerCase().includes(searchLower) ||
                (c.name && c.name.toLowerCase().includes(searchLower)) ||
                (c.phone && c.phone.includes(search)) ||
                (c.box_number && String(c.box_number).includes(search))
            );
            if (!matchesSearch) return false;
        }
        
        // Apply status filter
        if (activeFilter) {
            const statuses = customerStatuses[c.customer_id] || {};
            if (activeFilter === 'cu_observatii') {
                return customerNotes[c.customer_id]?.trim();
            }
            if (activeFilter === 'fara_bife') {
                // Clienți fără nicio bifă
                return !statuses.mai_sta && !statuses.asteapta_raspuns && !statuses.de_pregatit && 
                       !statuses.de_anulat && !statuses.de_intrebat && !statuses.de_livrat && !statuses.comanda_mica;
            }
            return statuses[activeFilter];
        }
        
        return true;
    }));

    // Generic print function with filtering
    const handlePrint = (filterType = null, title = null) => {
        // Use already filtered customers (based on activeFilter)
        let printList = filteredCustomers;
        let printTitle = title || 'Control Clienți';
        
        // Set title based on filter type
        if (filterType === 'de_pregatit') {
            printTitle = 'De Pregătit';
        } else if (filterType === 'asteapta_raspuns') {
            printTitle = 'Așteaptă Răspuns';
        } else if (filterType === 'cu_observatii') {
            printTitle = 'Cu Observații';
        } else if (filterType === 'de_anulat') {
            printTitle = 'De Anulat';
        } else if (filterType === 'de_intrebat') {
            printTitle = 'De Întrebat';
        } else if (filterType === 'de_livrat') {
            printTitle = 'De Livrat';
        } else if (filterType === 'comanda_mica') {
            printTitle = 'Comandă Mică';
        } else if (filterType === 'fara_bife') {
            printTitle = 'Fără Bife';
        }
        
        // Calculate total value
        const totalValue = printList.reduce((sum, c) => sum + c.total_value, 0);
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>${printTitle} - ${new Date().toLocaleDateString('ro-RO')}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 10px; margin: 0; }
                    h1 { text-align: center; margin-bottom: 10px; font-size: 16px; }
                    .subtitle { text-align: center; margin-bottom: 10px; font-size: 12px; color: #666; }
                    table { width: 100%; border-collapse: collapse; font-size: 11px; }
                    th, td { border: 1px solid #ccc; padding: 3px 5px; text-align: left; }
                    th { background: #f0f0f0; font-weight: bold; font-size: 10px; }
                    .col-id { width: 6%; }
                    .col-cutie { width: 6%; text-align: center; }
                    .col-name { width: 22%; }
                    .col-phone { width: 12%; }
                    .col-date { width: 8%; }
                    .col-qty { width: 6%; text-align: center; }
                    .col-val { width: 8%; text-align: right; }
                    .col-status { width: 14%; }
                    .col-note { width: 18%; }
                    .status-badge { 
                        display: inline-block; 
                        padding: 1px 4px; 
                        border-radius: 3px; 
                        font-size: 9px;
                        margin: 1px 0;
                    }
                    .status-mai_sta { background: #fef3c7; color: #92400e; }
                    .status-asteapta_raspuns { background: #dbeafe; color: #1e40af; }
                    .status-de_pregatit { background: #dcfce7; color: #166534; }
                    .status-de_anulat { background: #fee2e2; color: #991b1b; }
                    .status-de_intrebat { background: #cffafe; color: #0e7490; }
                    .status-de_livrat { background: #f3e8ff; color: #6b21a8; }
                    .status-comanda_mica { background: #ffedd5; color: #c2410c; }
                    .note { font-style: italic; color: #555; font-size: 9px; }
                    .timestamp { font-size: 8px; color: #999; display: block; }
                    .footer { text-align: center; margin-top: 10px; color: #666; font-size: 10px; }
                    @media print {
                        button { display: none; }
                        body { padding: 5px; }
                    }
                </style>
            </head>
            <body>
                <h1>${printTitle}</h1>
                <div class="subtitle">${new Date().toLocaleDateString('ro-RO')} | Valoare totală: ${formatPrice(totalValue)}</div>
                <table>
                    <thead>
                        <tr>
                            <th class="col-id">ID</th>
                            <th class="col-cutie">Cutie</th>
                            <th class="col-name">Nume</th>
                            <th class="col-phone">Telefon</th>
                            <th class="col-date">Data</th>
                            <th class="col-qty">Buc</th>
                            <th class="col-val">Valoare</th>
                            <th class="col-status">Status</th>
                            <th class="col-note">Observații</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${printList.map(c => {
                            const statuses = customerStatuses[c.customer_id] || {};
                            const statusBadges = STATUS_OPTIONS
                                .filter(s => statuses[s.id])
                                .map(s => `<span class="status-badge status-${s.id}">${s.label}<span class="timestamp">${formatStatusTime(statuses[s.id])}</span></span>`)
                                .join('');
                            
                            return `
                                <tr>
                                    <td class="col-id">${c.customer_id}</td>
                                    <td class="col-cutie">${c.box_number || '-'}</td>
                                    <td class="col-name">${c.name || '-'}</td>
                                    <td class="col-phone">${c.phone || '-'}</td>
                                    <td class="col-date">${formatDate(c.box_assigned_at || c.created_at)}</td>
                                    <td class="col-qty">${c.total_qty}</td>
                                    <td class="col-val">${formatPrice(c.total_value)}</td>
                                    <td class="col-status">${statusBadges || '-'}</td>
                                    <td class="col-note note">${customerNotes[c.customer_id] || '-'}</td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
                <p class="footer">
                    Total: ${printList.length} clienți | Generat: ${new Date().toLocaleString('ro-RO')}
                </p>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="h-screen bg-zinc-50 flex flex-col overflow-hidden">
            {/* FIXED HEADER SECTION */}
            <div className="flex-shrink-0 p-4 sm:p-6 pb-0">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h1 className="text-xl sm:text-2xl font-bold text-zinc-800 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Users size={24} className="text-indigo-500" />
                            Control Clienți
                        </h1>
                        <div className="flex gap-2">
                            <Button
                                onClick={resetAllStatuses}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1 border-red-300 text-red-600 hover:bg-red-50"
                            >
                                <RotateCcw size={16} />
                                Resetează Bife
                            </Button>
                            <Button
                                onClick={loadData}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                            >
                                <RefreshCw size={16} />
                                Reîncarcă
                            </Button>
                        </div>
                    </div>

                {/* Filter Buttons Row */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-sm text-zinc-500 flex items-center mr-1">Filtrare:</span>
                    <Button
                        onClick={() => setActiveFilter(null)}
                        variant={activeFilter === null ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === null ? 'bg-indigo-600' : ''}`}
                        size="sm"
                    >
                        Toți ({customers.length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'de_pregatit' ? null : 'de_pregatit')}
                        variant={activeFilter === 'de_pregatit' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'de_pregatit' ? 'bg-green-600' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                        size="sm"
                    >
                        De pregătit ({Object.values(customerStatuses).filter(s => s.de_pregatit).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'asteapta_raspuns' ? null : 'asteapta_raspuns')}
                        variant={activeFilter === 'asteapta_raspuns' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'asteapta_raspuns' ? 'bg-blue-600' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                        size="sm"
                    >
                        Așteaptă ({Object.values(customerStatuses).filter(s => s.asteapta_raspuns).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'de_anulat' ? null : 'de_anulat')}
                        variant={activeFilter === 'de_anulat' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'de_anulat' ? 'bg-red-600' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                        size="sm"
                    >
                        De anulat ({Object.values(customerStatuses).filter(s => s.de_anulat).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'de_intrebat' ? null : 'de_intrebat')}
                        variant={activeFilter === 'de_intrebat' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'de_intrebat' ? 'bg-cyan-600' : 'border-cyan-300 text-cyan-600 hover:bg-cyan-50'}`}
                        size="sm"
                    >
                        De întrebat ({Object.values(customerStatuses).filter(s => s.de_intrebat).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'de_livrat' ? null : 'de_livrat')}
                        variant={activeFilter === 'de_livrat' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'de_livrat' ? 'bg-purple-600' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
                        size="sm"
                    >
                        De livrat ({Object.values(customerStatuses).filter(s => s.de_livrat).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'comanda_mica' ? null : 'comanda_mica')}
                        variant={activeFilter === 'comanda_mica' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'comanda_mica' ? 'bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                        size="sm"
                    >
                        Comandă mică ({Object.values(customerStatuses).filter(s => s.comanda_mica).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'cu_observatii' ? null : 'cu_observatii')}
                        variant={activeFilter === 'cu_observatii' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'cu_observatii' ? 'bg-amber-600' : 'border-amber-300 text-amber-600 hover:bg-amber-50'}`}
                        size="sm"
                    >
                        Cu obs. ({Object.values(customerNotes).filter(n => n?.trim()).length})
                    </Button>
                    <Button
                        onClick={() => setActiveFilter(activeFilter === 'fara_bife' ? null : 'fara_bife')}
                        variant={activeFilter === 'fara_bife' ? 'default' : 'outline'}
                        className={`flex items-center gap-1 ${activeFilter === 'fara_bife' ? 'bg-zinc-600' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-100'}`}
                        size="sm"
                    >
                        Fără bife ({customers.filter(c => {
                            const s = customerStatuses[c.customer_id] || {};
                            return !s.mai_sta && !s.asteapta_raspuns && !s.de_pregatit && !s.de_anulat && !s.de_intrebat && !s.de_livrat && !s.comanda_mica;
                        }).length})
                    </Button>
                    
                    {/* Print current view button */}
                    <div className="border-l border-zinc-300 pl-2 ml-2">
                        <Button
                            onClick={() => handlePrint(activeFilter)}
                            className="bg-zinc-700 hover:bg-zinc-800 flex items-center gap-1"
                            size="sm"
                        >
                            <Printer size={16} />
                            Printează ({filteredCustomers.length})
                        </Button>
                    </div>
                </div>

                {/* Search and Sort Row */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            placeholder="Caută după ID, nume, telefon sau cutie..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <ArrowUpDown size={16} className="text-zinc-500" />
                        <span className="text-sm text-zinc-500">Sortare:</span>
                        <div className="flex gap-1">
                            <Button
                                onClick={() => setSortBy('box')}
                                variant={sortBy === 'box' ? 'default' : 'outline'}
                                size="sm"
                                className={sortBy === 'box' ? 'bg-indigo-600' : ''}
                            >
                                Cutie
                            </Button>
                            <Button
                                onClick={() => setSortBy('customer')}
                                variant={sortBy === 'customer' ? 'default' : 'outline'}
                                size="sm"
                                className={sortBy === 'customer' ? 'bg-indigo-600' : ''}
                            >
                                Client
                            </Button>
                            <Button
                                onClick={() => setSortBy('value')}
                                variant={sortBy === 'value' ? 'default' : 'outline'}
                                size="sm"
                                className={sortBy === 'value' ? 'bg-indigo-600' : ''}
                            >
                                Sumă
                            </Button>
                            <Button
                                onClick={() => setSortBy('status')}
                                variant={sortBy === 'status' ? 'default' : 'outline'}
                                size="sm"
                                className={sortBy === 'status' ? 'bg-indigo-600' : ''}
                            >
                                Status
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-2 mb-4 text-xs">
                    <span className="text-zinc-500">Legendă:</span>
                    {STATUS_OPTIONS.map(s => (
                        <span key={s.id} className={`px-2 py-1 rounded border ${s.color}`}>
                            {s.label}
                        </span>
                    ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white border border-zinc-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-indigo-600">{filteredCustomers.length}</div>
                        <p className="text-xs text-zinc-500">Clienți activi</p>
                    </div>
                    <div className="bg-white border border-zinc-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-emerald-600">
                            {filteredCustomers.reduce((sum, c) => sum + c.total_qty, 0)}
                        </div>
                        <p className="text-xs text-zinc-500">Total produse</p>
                    </div>
                    <div className="bg-white border border-zinc-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-amber-600">
                            {formatPrice(filteredCustomers.reduce((sum, c) => sum + c.total_value, 0))}
                        </div>
                        <p className="text-xs text-zinc-500">Valoare totală</p>
                    </div>
                    <div className="bg-white border border-zinc-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-green-600">
                            {Object.values(customerStatuses).filter(s => s.de_pregatit).length}
                        </div>
                        <p className="text-xs text-zinc-500">De pregătit</p>
                    </div>
                </div>
                </div>
            </div>
            
            {/* SCROLLABLE CUSTOMERS LIST */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4">
                <div className="max-w-7xl mx-auto">
                    <div id="print-content" className="space-y-3">
                    {loading ? (
                        <div className="text-center py-12 text-zinc-500">
                            <RefreshCw size={32} className="mx-auto mb-2 animate-spin" />
                            Se încarcă...
                        </div>
                    ) : filteredCustomers.length === 0 ? (
                        <div className="text-center py-12 text-zinc-500">
                            <Users size={48} className="mx-auto mb-4 text-zinc-300" />
                            <p>Nu există clienți cu rezervări/comenzi active</p>
                        </div>
                    ) : (
                        filteredCustomers.map(customer => {
                            const statuses = customerStatuses[customer.customer_id] || {};
                            const note = customerNotes[customer.customer_id] || '';
                            const isEditingThis = editingNote === customer.customer_id;
                            
                            // Determine background color based on phone
                            const hasPhone = customer.phone && customer.phone.trim();
                            const hasName = customer.name && customer.name.trim();
                            let bgClass = 'bg-white';
                            if (!hasPhone) {
                                bgClass = 'bg-red-50 border-red-200';
                            } else if (hasPhone && !hasName) {
                                bgClass = 'bg-blue-50 border-blue-200';
                            } else {
                                bgClass = 'bg-green-50 border-green-200';
                            }
                            
                            return (
                                <div 
                                    key={customer.customer_id}
                                    className={`${bgClass} border rounded-xl p-3 sm:p-4`}
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                        {/* Customer Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                {/* Box number - always show prominently */}
                                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-sm font-bold">
                                                    {customer.box_number || '-'}
                                                </span>
                                                <span className="text-lg font-bold text-zinc-800">
                                                    {customer.customer_id}
                                                </span>
                                                {customer.name && (
                                                    <span className="text-zinc-600 truncate">
                                                        {customer.name}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-500">
                                                {customer.phone && (
                                                    <span className="flex items-center gap-1">
                                                        <Phone size={14} />
                                                        {customer.phone}
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={14} />
                                                    {formatDate(customer.box_assigned_at || customer.created_at)}
                                                </span>
                                            </div>
                                            
                                            <div className="flex gap-4 mt-2 text-sm">
                                                <span className="text-emerald-600 font-medium">
                                                    {customer.total_qty} produse
                                                </span>
                                                <span className="text-amber-600 font-medium">
                                                    {formatPrice(customer.total_value)}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Status Checkboxes */}
                                        <div className="flex flex-wrap gap-2">
                                            {STATUS_OPTIONS.map(status => {
                                                const isChecked = !!statuses[status.id];
                                                const checkedAt = statuses[status.id];
                                                
                                                // Special handling for de_livrat with date picker
                                                if (status.hasDate) {
                                                    return (
                                                        <div key={status.id} className="flex flex-col items-center">
                                                            <Popover 
                                                                open={deliveryDatePicker === customer.customer_id && !isChecked}
                                                                onOpenChange={(open) => {
                                                                    if (!open) setDeliveryDatePicker(null);
                                                                }}
                                                            >
                                                                <PopoverTrigger asChild>
                                                                    <button
                                                                        onClick={() => {
                                                                            if (isChecked) {
                                                                                // Uncheck
                                                                                toggleStatus(customer.customer_id, status.id);
                                                                            } else {
                                                                                // Open date picker
                                                                                setDeliveryDatePicker(customer.customer_id);
                                                                            }
                                                                        }}
                                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1 ${
                                                                            isChecked 
                                                                                ? status.color + ' ring-2 ring-offset-1'
                                                                                : 'bg-zinc-100 text-zinc-500 border-zinc-200 hover:bg-zinc-200'
                                                                        }`}
                                                                    >
                                                                        {isChecked ? <CheckCircle2 size={12} /> : <Truck size={12} />}
                                                                        {status.label}
                                                                    </button>
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-auto p-0" align="end">
                                                                    <CalendarComponent
                                                                        mode="single"
                                                                        selected={parseDeliveryDate(checkedAt)}
                                                                        onSelect={(date) => {
                                                                            if (date) {
                                                                                toggleStatus(customer.customer_id, status.id, date);
                                                                            }
                                                                        }}
                                                                        initialFocus
                                                                    />
                                                                </PopoverContent>
                                                            </Popover>
                                                            {isChecked && checkedAt && (
                                                                <span className="text-[10px] text-purple-600 font-medium mt-0.5">
                                                                    {formatStatusTime(checkedAt)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                return (
                                                    <div key={status.id} className="flex flex-col items-center">
                                                        <button
                                                            onClick={() => toggleStatus(customer.customer_id, status.id)}
                                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                                isChecked 
                                                                    ? status.color + ' ring-2 ring-offset-1'
                                                                    : 'bg-zinc-100 text-zinc-500 border-zinc-200 hover:bg-zinc-200'
                                                            }`}
                                                        >
                                                            {isChecked && <CheckCircle2 size={12} className="inline mr-1" />}
                                                            {status.label}
                                                        </button>
                                                        {isChecked && checkedAt && (
                                                            <span className="text-[10px] text-zinc-400 mt-0.5">
                                                                {formatStatusTime(checkedAt)}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    
                                    {/* Notes Section */}
                                    <div className="mt-3 pt-3 border-t border-zinc-200">
                                        {isEditingThis ? (
                                            <div className="flex gap-2">
                                                <Textarea
                                                    value={tempNote}
                                                    onChange={(e) => setTempNote(e.target.value)}
                                                    placeholder="Scrie observații..."
                                                    className="flex-1 h-20 text-sm"
                                                    autoFocus
                                                />
                                                <div className="flex flex-col gap-1">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => saveNote(customer.customer_id)}
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                    >
                                                        Salvează
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setEditingNote(null)}
                                                    >
                                                        Anulează
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div 
                                                className="flex items-start gap-2 cursor-pointer hover:bg-zinc-50 rounded p-2 -m-2"
                                                onClick={() => {
                                                    setEditingNote(customer.customer_id);
                                                    setTempNote(note);
                                                }}
                                            >
                                                <MessageSquare size={16} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                                                {note ? (
                                                    <p className="text-sm text-zinc-600">{note}</p>
                                                ) : (
                                                    <p className="text-sm text-zinc-400 italic">Click pentru a adăuga observații...</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ControlClienti;
